import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDOP, formatDate } from "@/lib/format";

const sel = "mt-1 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm";

const ACTION_LABEL = (a) => ({
  nota_credito_E34: `Nota de crédito ${a.ncf}`,
  factura_correcta: `Factura correcta ${a.ncf} (ITBIS por cobrar ${formatDOP(a.itbis_por_cobrar)})`,
  itbis_asumido: `ITBIS asumido ${formatDOP(a.amount)} + recargos/intereses ${formatDOP(a.surcharge_interest)}`,
}[a.type] || a.type);

function CorrectDialog({ inv, onClose, onDone }) {
  const [f, setF] = useState({ reason: "", correct_type: "E31", customer_unavailable: false, responsibility: "operativa", responsible_email: inv.cashier || "", surcharge: 0, interest: 0 });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/exempt-error`, { ...f, surcharge: Number(f.surcharge), interest: Number(f.interest) });
      toast.success(data.label); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="exempt-correct-dialog">
        <DialogHeader><DialogTitle>Corregir venta exenta inválida · {inv.ncf}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label className="overline">Motivo</Label><Input className="mt-1" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="exempt-reason" /></div>
          <label className="col-span-2 flex items-center gap-2"><Checkbox checked={f.customer_unavailable} onCheckedChange={(v) => setF({ ...f, customer_unavailable: !!v })} data-testid="exempt-unavailable" />El cliente no está disponible: el emisor asume el ITBIS</label>
          {f.customer_unavailable ? (<>
            <div><Label className="overline">Recargos</Label><Input type="number" className="mt-1" value={f.surcharge} onChange={(e) => setF({ ...f, surcharge: e.target.value })} data-testid="exempt-surcharge" /></div>
            <div><Label className="overline">Intereses</Label><Input type="number" className="mt-1" value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })} data-testid="exempt-interest" /></div>
          </>) : (
            <div className="col-span-2"><Label className="overline">Factura correcta</Label>
              <select className={sel} value={f.correct_type} onChange={(e) => setF({ ...f, correct_type: e.target.value })} data-testid="exempt-correct-type">
                <option value="E31">E31 · Crédito fiscal</option><option value="E32">E32 · Consumo</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">Amelia emite la E34 que anula {inv.ncf} y la nueva factura con ITBIS; el ITBIS queda por cobrar al cliente.</p></div>
          )}
          <div><Label className="overline">Responsabilidad</Label>
            <select className={sel} value={f.responsibility} onChange={(e) => setF({ ...f, responsibility: e.target.value })} data-testid="exempt-responsibility">
              <option value="ninguna">Ninguna</option><option value="operativa">Operativa</option><option value="financiera">Financiera</option>
            </select></div>
          <div><Label className="overline">Responsable</Label><Input className="mt-1" value={f.responsible_email} onChange={(e) => setF({ ...f, responsible_email: e.target.value })} data-testid="exempt-responsible" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-primary font-bold" disabled={busy || !f.reason} onClick={save} data-testid="exempt-correct-save">Registrar corrección</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RiskTab() {
  const [rows, setRows] = useState([]);
  const [fix, setFix] = useState(null);
  const load = async () => setRows((await api.get("/fiscal/exempt-risk")).data);
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Facturas exentas (E44/E46) emitidas a clientes que hoy no están autorizados, o sin exportación marcada.</p>
      <table className="w-full text-sm border border-border rounded-md" data-testid="exempt-risk-table">
        <thead className="overline border-b"><tr><th className="p-2 text-left">e-NCF</th><th className="p-2 text-left">Cliente</th><th className="p-2 text-left">Fecha</th><th className="p-2 text-left">Cajero · turno · caja</th><th className="p-2 text-right">Total</th><th className="p-2" /></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-b border-border/60" data-testid={`exempt-risk-row-${r.id}`}>
            <td className="p-2 font-mono">{r.ncf}</td><td className="p-2">{r.customer_name}</td><td className="p-2 text-xs">{formatDate(r.created_at)}</td>
            <td className="p-2 text-xs">{r.cashier} · {r.turno || "—"} · {r.caja || "—"}</td><td className="p-2 text-right font-mono">{formatDOP(r.total)}</td>
            <td className="p-2 text-right">{r.corrected ? <span className="text-xs text-success font-bold">Corregida</span> :
              <Button size="sm" variant="outline" onClick={() => setFix(r)} data-testid={`exempt-fix-${r.id}`}>Corregir</Button>}</td>
          </tr>))}
          {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Sin ventas exentas en riesgo</td></tr>}
        </tbody>
      </table>
      {fix && <CorrectDialog inv={fix} onClose={() => setFix(null)} onDone={() => { setFix(null); load(); }} />}
    </div>
  );
}

export function EventsTab() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ cashier: "", turno: "", caja: "", date_from: "", date_to: "" });
  const load = async () => setRows((await api.get("/fiscal/events", { params: f })).data);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Input placeholder="Cajero (email)" value={f.cashier} onChange={(e) => setF({ ...f, cashier: e.target.value })} data-testid="events-cashier" />
        <Input placeholder="Turno" value={f.turno} onChange={(e) => setF({ ...f, turno: e.target.value })} data-testid="events-turno" />
        <Input placeholder="Caja" value={f.caja} onChange={(e) => setF({ ...f, caja: e.target.value })} data-testid="events-caja" />
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="events-from" />
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="events-to" />
        <Button variant="outline" onClick={load} data-testid="events-filter">Filtrar</Button>
      </div>
      <table className="w-full text-sm border border-border rounded-md" data-testid="events-table">
        <thead className="overline border-b"><tr><th className="p-2 text-left">Evento</th><th className="p-2 text-left">Factura</th><th className="p-2 text-left">Cajero</th><th className="p-2 text-left">Turno · caja · sucursal</th><th className="p-2 text-left">Emitida</th><th className="p-2 text-right">ITBIS omitido</th><th className="p-2 text-left">Responsabilidad</th><th className="p-2 text-left">Acciones</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-b border-border/60 align-top">
            <td className="p-2 text-xs font-bold text-destructive">{r.label}</td><td className="p-2 font-mono text-xs">{r.invoice_ncf}</td>
            <td className="p-2 text-xs">{r.cashier_email}</td><td className="p-2 text-xs">{r.turno || "—"} · {r.caja || "—"} · {r.sucursal || "—"}</td>
            <td className="p-2 text-xs">{formatDate(r.issued_at)}<div className="text-muted-foreground">{r.mode} {r.ip}</div></td>
            <td className="p-2 text-right font-mono">{formatDOP(r.omitted_itbis)}</td>
            <td className="p-2 text-xs">{r.responsibility} · {r.responsible_email}</td>
            <td className="p-2 text-xs">{(r.actions || []).map((a) => ACTION_LABEL(a)).join(" · ")}</td>
          </tr>))}
          {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Sin eventos</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
